import { useEffect } from "react";

// Inject SEO-relevant meta tags + JSON-LD structured data once at page mount.
// Improves Google Quality Score, rich snippets and social sharing previews.

const META = [
  { name: "description", content: "Online-Weiterbildungen Pflege mit Bildungsgutschein – AZAV zertifiziert, Start jeden Montag. LG1/LG2, PDL, Praxisanleitung, Wundexperte & mehr. Jetzt kostenlose Beratung sichern." },
  { name: "keywords", content: "Weiterbildung Pflege, Bildungsgutschein Pflege, LG1 LG2 Pflege, Pflegehelfer Online, PDL Weiterbildung, AZAV zertifiziert, Online Pflege Schulung, Pflegehilfskraft, Pflegedienstleitung, Wundexperte, Pflichtfortbildung Pflege, Dickies-Campus" },
  { name: "robots", content: "index,follow,max-image-preview:large" },
  { name: "author", content: "Dickies-Campus GmbH" },
  { name: "theme-color", content: "#3A332E" },
  { property: "og:type", content: "website" },
  { property: "og:site_name", content: "Dickies-Campus" },
  { property: "og:title", content: "Dickies-Campus – AZAV Weiterbildung Pflege mit Bildungsgutschein" },
  { property: "og:description", content: "Online-Weiterbildung Pflege, 100% online via Zoom, Start jeden Montag. AZAV zertifiziert, Bildungsgutschein möglich. LG1/LG2, PDL, Wundexperte & mehr." },
  { property: "og:image", content: "/images/z5c47tmw.webp" },
  { property: "og:url", content: "https://www.dickies-helden.de" },
  { property: "og:locale", content: "de_DE" },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: "Dickies-Campus – Weiterbildung Pflege mit Bildungsgutschein" },
  { name: "twitter:description", content: "AZAV zertifiziert. 100% online. Start jeden Montag. Bildungsgutschein möglich." },
  { name: "twitter:image", content: "/images/z5c47tmw.webp" },
];

function setMeta({ name, property, content }) {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SeoHead({ courses = [], faqs = [] }) {
  useEffect(() => {
    META.forEach(setMeta);
    setCanonical("https://www.dickies-helden.de/");

    setJsonLd("jsonld-org", {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "Dickies-Campus",
      "legalName": "Dickies-Campus GmbH",
      "url": "https://www.dickies-helden.de",
      "logo": "/images/z5c47tmw.webp",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Neuenhausplatz 73",
        "postalCode": "40699",
        "addressLocality": "Erkrath",
        "addressCountry": "DE",
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+49-211-29273330",
        "contactType": "customer support",
        "email": "info@dickies-helden.de",
        "areaServed": "DE",
        "availableLanguage": ["de"],
      },
      "sameAs": [],
    });

    if (courses.length > 0) {
      setJsonLd("jsonld-courses", {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": courses.slice(0, 17).map((c, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "item": {
            "@type": "Course",
            "name": c.title,
            "description": c.description || c.subtitle,
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Dickies-Campus",
              "sameAs": "https://www.dickies-helden.de",
            },
            "offers": {
              "@type": "Offer",
              "price": c.price?.replace(/[^0-9.,]/g, "").replace(",", ".") || "0",
              "priceCurrency": "EUR",
            },
          },
        })),
      });
    }

    if (faqs.length > 0) {
      setJsonLd("jsonld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      });
    }
  }, [courses, faqs]);

  return null;
}
